import { isChrome, isEdge } from "react-device-detect";

export type ProctoringSettings = {
  enabled: boolean;
  audioCapture: boolean;
  screenCapture: boolean;
};

export type GenerateSignedUrlRequest = {
  assessment: string;
};

export type SignedUrl = {
  url: string;
  signature: string;
};

export type CreateMetaData = {
  eventId: string;
  fileName: string;
  capturedAt: number;
  assessment: string;
  order: number;
};

export type CreatePermissions = {
  assessment: string;
  camera: boolean;
  microphone: boolean;
  screen: boolean;
  redirected: boolean;
};

const AUDIO_CAPTURE_INTERVAL = 60000;
const AUDIO_CAPTURE_LENGTH = 15000;
const CHECK_PERMISSIONS_INTERVAL = 5000;
const IMAGE_CAPTURE_INTERVAL = 5000;
const SCREEN_CAPTURE_INTERVAL = 6000;
const RETRY_INTERVAL = 5000;

const IMAGE_CAPTURE_HEIGHT = 480;
const IMAGE_CAPTURE_WIDTH = 640;
const SCREEN_CAPTURE_HEIGHT = 720;
const SCREEN_CAPTURE_WIDTH = 1280;

const INACTIVITY_TIMEOUT = 5 * 60 * 1000;

const VIDEO_CONFIG = {
  width: { ideal: IMAGE_CAPTURE_WIDTH, max: IMAGE_CAPTURE_WIDTH },
  facingMode: "user"
};

const SCREEN_CONFIG = {
  displaySurface: "monitor"
};

export default class Capture {
  private gameName: string;
  private eventId: string;
  private endpointProctoringApi: string;
  private enabled: boolean;
  private audioCapture: boolean;
  private screenCapture: boolean;
  private audioStream!: MediaStream;
  private mediaStream!: MediaStream;
  private screenStream!: MediaStream;
  private videoStream!: MediaStream;
  private imageCaptureInterval!: NodeJS.Timeout;
  private audioCaptureStartInterval!: NodeJS.Timeout;
  private audioCaptureStopInterval!: NodeJS.Timeout;
  private screenCaptureInterval!: NodeJS.Timeout;
  private checkPermissionsInterval!: NodeJS.Timeout;
  private isCameraEnabled: boolean;
  private isMicrophoneEnabled: boolean;
  private isScreenEnabled: boolean;
  private signedUrl!: SignedUrl;
  private hmacSecurityKey: string;
  private isMobile: boolean;
  private inactivityCheckInterval!: NodeJS.Timeout;
  private static allowCapture: boolean;
  private static lastInteractionTime: number = Date.now();
  private static lastFaceDetectedTime: number = Date.now();
  private static isProctoringPaused: boolean = false;
  private static initialized: boolean = false;
  private static hasRequestedScreenCapture: boolean = false;
  private interactionEvents: string[] = [
    "mousedown",
    "keydown",
    "touchstart",
    "click"
  ];
  private updateInteractionTime: () => void = () => {};

  private get allowCapture(): boolean {
    return Capture.allowCapture;
  }

  private set allowCapture(value: boolean) {
    Capture.allowCapture = value;
  }

  private get lastInteractionTime(): number {
    return Capture.lastInteractionTime;
  }

  private set lastInteractionTime(value: number) {
    Capture.lastInteractionTime = value;
  }

  private get lastFaceDetectedTime(): number {
    return Capture.lastFaceDetectedTime;
  }

  private set lastFaceDetectedTime(value: number) {
    Capture.lastFaceDetectedTime = value;
  }

  private get isProctoringPaused(): boolean {
    return Capture.isProctoringPaused;
  }

  private set isProctoringPaused(value: boolean) {
    Capture.isProctoringPaused = value;
  }

  private audioCounter: number = 1;
  private imageCounter: number = 1;
  private screenCounter: number = 1;

  constructor(
    gameName: string,
    eventId: string,
    endpointProctoringApi: string,
    proctoring: ProctoringSettings,
    hmacSecurityKey: string,
    isMobile: boolean
  ) {
    this.gameName = gameName;
    this.eventId = eventId;
    this.endpointProctoringApi = endpointProctoringApi;
    this.enabled = proctoring?.enabled ?? false;
    this.audioCapture = proctoring?.audioCapture ?? false;
    this.screenCapture = proctoring?.screenCapture ?? false;
    this.allowCapture = false;
    this.isCameraEnabled = false;
    this.isMicrophoneEnabled = false;
    this.isScreenEnabled = false;
    this.hmacSecurityKey = hmacSecurityKey;
    this.isMobile = isMobile;

    // If mobile, disable screen capture
    if (this.isMobile) {
      this.screenCapture = false;
    }

    this.configureCanvas();
  }

  public disable(): void {
    this.disableCameraStream();
    this.disableAudioStream();
    this.disableScreenStream();
    clearInterval(this.checkPermissionsInterval);
    clearInterval(this.inactivityCheckInterval);

    this.mediaStream?.getTracks().forEach(track => track.stop());
    this.enabled = false;
  }

  private disableCameraStream(): void {
    clearInterval(this.imageCaptureInterval);
    this.videoStream?.getVideoTracks().forEach(track => track.stop());
  }

  private disableAudioStream(): void {
    clearInterval(this.audioCaptureStartInterval);
    clearInterval(this.audioCaptureStopInterval);
    this.audioStream?.getAudioTracks().forEach(track => track.stop());
  }

  private disableScreenStream(): void {
    clearInterval(this.screenCaptureInterval);
    this.screenStream?.getTracks().forEach(track => track.stop());
    if (this.screenStream) {
      this.screenStream.getTracks().forEach(track => track.stop());
    }
  }

  public async enable(): Promise<void> {
    this.signedUrl = await this.generateSignedUrl();
    await this.enableStreams();
    this.enableInteractionTracking();
    this.startInactivityCheck();
  }

  private enableInteractionTracking(): void {
    this.updateInteractionTime = (): void => {
      this.lastInteractionTime = Date.now();

      if (this.isProctoringPaused) {
        this.resumeProctoring();
      }
    };

    this.interactionEvents.forEach(eventType => {
      window.addEventListener(eventType, this.updateInteractionTime, {
        passive: true
      });
    });
  }

  // Check every 30 seconds to see if user face is detected or has interacted in last 5 minutes
  private startInactivityCheck(): void {
    if (this.inactivityCheckInterval) {
      clearInterval(this.inactivityCheckInterval);
    }
    this.inactivityCheckInterval = setInterval(() => {
      if (!this.allowCapture) {
        this.lastFaceDetectedTime = Date.now();
        return;
      }
      const currentTime = Date.now();
      const timeSinceLastInteraction = currentTime - this.lastInteractionTime;
      const timeSinceLastFaceDetected = currentTime - this.lastFaceDetectedTime;

      if (
        timeSinceLastInteraction >= INACTIVITY_TIMEOUT &&
        timeSinceLastFaceDetected >= INACTIVITY_TIMEOUT &&
        !this.isProctoringPaused
      ) {
        this.pauseProctoring();
      }
    }, 30000);
  }

  private pauseProctoring(): void {
    this.isProctoringPaused = true;
  }

  private resumeProctoring(): void {
    this.isProctoringPaused = false;
  }

  public toggleCapture(toggle: boolean): void {
    this.allowCapture = toggle;
  }

  public static async requestCameraPermissions(
    cameraLabel?: string
  ): Promise<boolean> {
    try {
      if (cameraLabel) {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(
          device => device.kind === "videoinput"
        );
        const cameraDevice = videoDevices.find(
          device => device.label === cameraLabel
        );

        if (cameraDevice) {
          await navigator.mediaDevices.getUserMedia({
            video: { deviceId: { exact: cameraDevice.deviceId } }
          });
          return true;
        }
      }

      // Fall back to default camera if no label provided or matching device not found
      await navigator.mediaDevices.getUserMedia({
        video: true
      });
      return true;
    } catch (err) {
      return false;
    }
  }

  private configureCanvas(): void {
    if (this.enabled) {
      // Create video element
      const video = document.createElement("video");
      video.id = "video";
      video.muted = true;
      video.autoplay = true;
      video.playsInline = true;
      video.style.display = "none";

      // Create canvas element
      const videoCanvas = document.createElement("canvas");
      videoCanvas.className = "canvas";
      videoCanvas.id = "videoCanvas";
      videoCanvas.style.display = "none";
      videoCanvas.width = IMAGE_CAPTURE_WIDTH;
      videoCanvas.height = IMAGE_CAPTURE_HEIGHT;

      // Append elements to the document body or another specific element
      document.body.appendChild(video);
      document.body.appendChild(videoCanvas);

      if (this.screenCapture) {
        // Create screen capture element
        const screen = document.createElement("video");
        screen.id = "screen";
        screen.style.display = "none";

        // Create canvas element
        const screenCanvas = document.createElement("canvas");
        screenCanvas.className = "canvas";
        screenCanvas.id = "screenCanvas";
        screenCanvas.style.display = "none";
        screenCanvas.width = SCREEN_CAPTURE_WIDTH;
        screenCanvas.height = SCREEN_CAPTURE_HEIGHT;

        document.body.appendChild(screen);
        document.body.appendChild(screenCanvas);
      }
    }
  }

  private hasPermissionResetCookie(): boolean {
    const cookieValue = document.cookie
      .split("; ")
      .find(row => row.startsWith("sessionData="));
    if (cookieValue) {
      try {
        const sessionData = JSON.parse(
          decodeURIComponent(cookieValue.split("=")[1])
        );
        if (sessionData?.proctoringPermissionsReset === true) {
          Capture.allowCapture = false;
          return true;
        }
      } catch (err) {
        console.error("Error parsing sessionData cookie:", err);
      }
    }

    return false;
  }

  private redirectToOverview(): void {
    if (this.hasPermissionResetCookie()) {
      return;
    }

    const url = new URL(`${window.location.origin}/e/${this.eventId}/overview`);
    const params = new URLSearchParams(url.search);
    params.set("proctoring", "false");
    params.delete("assessment");
    url.search = params.toString();
    window.location.replace(url.toString());
  }

  private async enableStreams(): Promise<void> {
    // If already initialized, don't reinitialize streams - just set up the permissions check
    if (Capture.initialized) {
      this.checkPermissionsInterval = setInterval(() => {
        this.checkPermissions();
      }, CHECK_PERMISSIONS_INTERVAL);
      return;
    }

    // Set the flag before any async operations to prevent race conditions
    Capture.initialized = true;

    if (this.enabled || this.audioCapture) {
      await this.enableMediaStream(true);
    }
    this.checkPermissionsInterval = setInterval(() => {
      this.checkPermissions();
    }, CHECK_PERMISSIONS_INTERVAL);
  }

  private async enableMediaStream(init = false): Promise<void> {
    try {
      // grab an explicit cameraLabel from the hash if present
      const hash = window.location.hash.substring(1);
      const params = new URLSearchParams(hash);
      const encodedCameraLabel = params.get("cameraLabel");
      let cameraId: string | undefined;

      // ask the browser to unlock the device(s) first
      const permissionsGranted = await Capture.requestCameraPermissions(
        encodedCameraLabel ?? undefined
      );
      if (!permissionsGranted) {
        await this.savePermissions(
          this.isCameraEnabled,
          this.isMicrophoneEnabled,
          this.isScreenEnabled,
          true
        );
        this.redirectToOverview();
        throw new Error("Camera permissions denied");
      }

      // if they passed a label, find its deviceId
      if (encodedCameraLabel) {
        const cameraLabel = decodeURIComponent(encodedCameraLabel);
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(d => d.kind === "videoinput");
        cameraId = videoDevices.find(d => d.label === cameraLabel)?.deviceId;
      }

      // build constraints so we only ever ask for what we actually need
      const constraints: MediaStreamConstraints = {
        video: this.enabled
          ? cameraId
            ? { deviceId: { exact: cameraId } }
            : VIDEO_CONFIG
          : false,
        audio: this.audioCapture
      };

      try {
        this.mediaStream = await navigator.mediaDevices.getUserMedia(
          constraints
        );

        // only hook up video capture if we actually asked for video
        if (constraints.video) {
          await this.enableVideoCapture(init);
        }
        // only hook up audio capture if we asked for mic
        if (constraints.audio) {
          await this.enableAudioCapture(init);
        }
      } catch (mediaError) {
        await this.savePermissions(
          this.isCameraEnabled,
          this.isMicrophoneEnabled,
          this.isScreenEnabled,
          true
        );
        this.redirectToOverview();
        throw mediaError;
      }
    } catch (err) {
      console.error(err);
      // retry on non‐permission errors
      if (err instanceof Error && !err.message.includes("permissions denied")) {
        setTimeout(() => this.retryConnectToMediaStream(), RETRY_INTERVAL);
      }
    }
  }

  private async enableAudioCapture(init = false): Promise<void> {
    try {
      this.audioStream = new MediaStream([
        this.mediaStream.getAudioTracks()[0]
      ]);
      if (this.audioStream) {
        if (init) {
          this.isMicrophoneEnabled = true;
          await this.savePermissions(
            this.isCameraEnabled,
            this.isMicrophoneEnabled,
            this.isScreenEnabled
          );
        }
        const mediaRecorder = new MediaRecorder(this.audioStream);
        const chunks: Blob[] = [];
        mediaRecorder.addEventListener("dataavailable", event => {
          chunks.push(event.data);
        });
        mediaRecorder.addEventListener("stop", () =>
          this.processAudio(chunks, this.audioCounter++)
        );
        mediaRecorder.start();
        this.audioCaptureStartInterval = setTimeout(() => {
          this.enableAudioCapture();
        }, AUDIO_CAPTURE_INTERVAL);
        this.audioCaptureStopInterval = setTimeout(() => {
          mediaRecorder.stop();
        }, AUDIO_CAPTURE_LENGTH);
      } else {
        setTimeout(
          () => this.retryConnectToMediaStream(),
          AUDIO_CAPTURE_INTERVAL
        );
      }
    } catch (err) {
      console.log(err);
      setTimeout(() => this.retryConnectToMediaStream(), RETRY_INTERVAL);
    }
  }

  public async enableScreenCapture(init = false): Promise<void> {
    // Skip if we've already requested screen capture
    if (Capture.hasRequestedScreenCapture) {
      return;
    }
    try {
      Capture.hasRequestedScreenCapture = true;
      this.screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: SCREEN_CONFIG
      });

      const screen = document.querySelector("#screen") as HTMLVideoElement;
      if (screen && this.screenStream) {
        if (init) {
          this.isScreenEnabled = true;
          await this.savePermissions(
            this.isCameraEnabled,
            this.isMicrophoneEnabled,
            this.isScreenEnabled
          );
        }
        screen.srcObject = this.screenStream;
        screen.onloadedmetadata = (): void => {
          screen.play();
        };
        clearInterval(this.screenCaptureInterval);
        this.screenCaptureInterval = setInterval(
          () => this.captureScreen(this.screenCounter++),
          SCREEN_CAPTURE_INTERVAL
        );
      } else {
        setTimeout(
          () => this.retryConnectToScreenStream(),
          SCREEN_CAPTURE_INTERVAL
        );
      }
    } catch (err) {
      console.log(err);
      // If this is a permission error and screen capture is required, redirect
      if (
        this.screenCapture &&
        err instanceof DOMException &&
        (err.name === "NotAllowedError" || err.name === "PermissionDeniedError")
      ) {
        await this.savePermissions(
          this.isCameraEnabled,
          this.isMicrophoneEnabled,
          this.isScreenEnabled,
          true
        );
        this.redirectToOverview();
        return;
      }

      // Only retry if not explicitly denied
      if (this.isScreenEnabled !== false) {
        setTimeout(() => this.retryConnectToScreenStream(), RETRY_INTERVAL);
      }
    }
  }

  private async enableVideoCapture(init = false): Promise<void> {
    try {
      this.videoStream = new MediaStream([
        this.mediaStream.getVideoTracks()[0]
      ]);
      const video = document.querySelector("#video") as HTMLVideoElement;
      if (video && this.videoStream) {
        if (init) {
          this.isCameraEnabled = true;
          await this.savePermissions(
            this.isCameraEnabled,
            this.isMicrophoneEnabled,
            this.isScreenEnabled
          );
        }
        video.srcObject = this.videoStream;
        video.onloadedmetadata = (): void => {
          video.play();
        };
        clearInterval(this.imageCaptureInterval);
        this.imageCaptureInterval = setInterval(
          () => this.captureImage(this.imageCounter++),
          IMAGE_CAPTURE_INTERVAL
        );
      } else {
        setTimeout(
          () => this.retryConnectToMediaStream(),
          IMAGE_CAPTURE_INTERVAL
        );
      }
    } catch (err) {
      console.log(err);
      setTimeout(() => this.retryConnectToMediaStream(), RETRY_INTERVAL);
    }
  }

  private async checkPermissions(): Promise<void> {
    const { cameraEnabled, microphoneEnabled, screenEnabled } =
      this.getEnabledPermissions();

    if (
      cameraEnabled !== this.isCameraEnabled ||
      microphoneEnabled !== this.isMicrophoneEnabled ||
      screenEnabled !== this.isScreenEnabled
    ) {
      const cameraRevoked = this.isCameraEnabled && !cameraEnabled;
      const microphoneRevoked = this.isMicrophoneEnabled && !microphoneEnabled;
      const screenRevoked = this.isScreenEnabled && !screenEnabled;

      this.isCameraEnabled = cameraEnabled;
      this.isMicrophoneEnabled = microphoneEnabled;
      this.isScreenEnabled = screenEnabled;
      const redirected = cameraRevoked || microphoneRevoked || screenRevoked;
      const shouldRedirect = redirected && !this.hasPermissionResetCookie();

      await this.savePermissions(
        this.isCameraEnabled,
        this.isMicrophoneEnabled,
        this.isScreenEnabled,
        redirected
      );

      // Handle specific media stream disabling when permissions are revoked
      if (cameraRevoked) {
        this.disableCameraStream();
      }

      if (microphoneRevoked) {
        this.disableAudioStream();
      }

      if (screenRevoked) {
        this.disableScreenStream();
      }

      // Only redirect if permissions were revoked AND we don't have the reset cookie
      if (shouldRedirect) {
        this.redirectToOverview();
      }
    }
  }

  private async retryConnectToMediaStream(): Promise<void> {
    try {
      if (!this.audioStream) {
        clearInterval(this.audioCaptureStartInterval);
        clearInterval(this.audioCaptureStopInterval);
        await this.enableAudioCapture();
      }
      if (!this.videoStream) {
        clearInterval(this.imageCaptureInterval);
        await this.enableVideoCapture();
      }
    } catch (err) {
      console.log(err);
      this.enableMediaStream();
    }
  }

  private async retryConnectToScreenStream(): Promise<void> {
    clearInterval(this.screenCaptureInterval);
    // Only retry if screen sharing was previously enabled and not explicitly denied
    if (this.isScreenEnabled !== false) {
      await this.enableScreenCapture();
    }
  }

  private async captureImage(imageCounter: number): Promise<void> {
    try {
      // Only attempt to capture if we have an active video stream and camera is allowed
      if (!this.videoStream?.active) {
        this.retryConnectToMediaStream();
        return;
      }

      // Check the specific camera permission flag instead of global allowCapture
      if (this.enabled && this.isCameraEnabled && this.allowCapture) {
        if (Capture.isProctoringPaused) {
          return;
        }
        const videoCanvas = document.querySelector(
          "#videoCanvas"
        ) as HTMLCanvasElement;
        const video = document.querySelector("#video") as HTMLVideoElement;
        if (videoCanvas && video) {
          const context = videoCanvas.getContext("2d");
          context?.drawImage(
            video,
            0,
            0,
            videoCanvas.width,
            videoCanvas.height
          );
          const b64Image = videoCanvas.toDataURL("image/png");
          const blob = await (await fetch(b64Image)).blob();
          const capturedAt = Date.now();
          const fileName = `webcam_${capturedAt}_${this.eventId}.png`;
          const assetUrl = `${this.signedUrl.url}/${fileName}?${this.signedUrl.signature}`;
          await this.saveViaSignedUrl(assetUrl, blob);
          await this.saveImageMetaData(fileName, capturedAt, imageCounter);
        }
      }
    } catch (err) {
      console.log(err);
      this.enableStreams();
    }
  }

  private async captureScreen(screenCounter: number): Promise<void> {
    try {
      // Only attempt to capture if we have an active screen stream and screen capture is allowed
      if (!this.screenStream?.active) {
        this.retryConnectToScreenStream();
        return;
      }

      // Check the specific screen permission flag instead of global allowCapture
      if (this.screenCapture && this.isScreenEnabled && this.allowCapture) {
        if (Capture.isProctoringPaused) {
          return;
        }
        const screenCanvas = document.querySelector(
          "#screenCanvas"
        ) as HTMLCanvasElement;
        const screen = document.querySelector("#screen") as HTMLVideoElement;
        if (screenCanvas && screen) {
          const context = screenCanvas.getContext("2d");
          context?.drawImage(
            screen,
            0,
            0,
            screenCanvas.width,
            screenCanvas.height
          );
          const b64Image = screenCanvas.toDataURL("image/png");
          const blob = await (await fetch(b64Image)).blob();
          const capturedAt = Date.now();
          const fileName = `screencap_${capturedAt}_${this.eventId}.png`;
          const assetUrl = `${this.signedUrl.url}/${fileName}?${this.signedUrl.signature}`;
          await this.saveViaSignedUrl(assetUrl, blob);
          await this.saveScreenMetaData(fileName, capturedAt, screenCounter);
        }
      }
    } catch (err) {
      console.log(err);
      this.enableStreams();
    }
  }

  private async processAudio(
    chunks: Blob[],
    audioCounter: number
  ): Promise<void> {
    // Check the specific audio permission flag instead of global allowCapture
    if (this.audioCapture && this.isMicrophoneEnabled && this.allowCapture) {
      if (Capture.isProctoringPaused) {
        return;
      }
      const blob = new Blob(chunks, { type: "audio/webm" });
      const capturedAt = Date.now();
      const fileName = `audio_${capturedAt}_${this.eventId}.webm`;
      const assetUrl = `${this.signedUrl.url}/${fileName}?${this.signedUrl.signature}`;
      await this.saveViaSignedUrl(assetUrl, blob);
      await this.saveAudioMetaData(fileName, capturedAt, audioCounter);
    }
  }

  private async generateSignedUrl(): Promise<SignedUrl> {
    const response = await fetch(
      `${this.endpointProctoringApi}/event/${this.eventId}/signed-url`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.hmacSecurityKey}`
        },
        body: JSON.stringify({
          assessment: this.gameName
        } as GenerateSignedUrlRequest)
      }
    );
    return await response.json();
  }

  private async saveViaSignedUrl(signedUrl: string, body: Blob): Promise<void> {
    await fetch(signedUrl, {
      method: "PUT",
      body
    });
  }

  private async saveAudioMetaData(
    fileName: string,
    capturedAt: number,
    audioCounter: number
  ): Promise<void> {
    await fetch(`${this.endpointProctoringApi}/event/${this.eventId}/audio`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.hmacSecurityKey}`
      },
      body: JSON.stringify({
        eventId: this.eventId,
        fileName,
        capturedAt,
        assessment: this.gameName,
        order: audioCounter
      } as CreateMetaData)
    });
  }

  private async saveImageMetaData(
    fileName: string,
    capturedAt: number,
    imageCounter: number
  ): Promise<{
    faceDetected: boolean;
  }> {
    const createImageResponse = await fetch(
      `${this.endpointProctoringApi}/event/${this.eventId}/image`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.hmacSecurityKey}`
        },
        body: JSON.stringify({
          eventId: this.eventId,
          fileName,
          capturedAt,
          assessment: this.gameName,
          order: imageCounter
        } as CreateMetaData)
      }
    );

    const responseData = await createImageResponse.json();
    const faceDetected = responseData.faceDetected;

    if (faceDetected) {
      this.lastFaceDetectedTime = Date.now();
    }

    return {
      faceDetected: faceDetected || false
    };
  }

  private async saveScreenMetaData(
    fileName: string,
    capturedAt: number,
    screenCounter: number
  ): Promise<void> {
    await fetch(`${this.endpointProctoringApi}/event/${this.eventId}/screen`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.hmacSecurityKey}`
      },
      body: JSON.stringify({
        eventId: this.eventId,
        fileName,
        capturedAt,
        assessment: this.gameName,
        order: screenCounter
      } as CreateMetaData)
    });
  }

  private async savePermissions(
    camera = false,
    microphone = false,
    screen = false,
    redirected = false
  ): Promise<void> {
    await fetch(
      `${this.endpointProctoringApi}/event/${this.eventId}/permissions`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.hmacSecurityKey}`
        },
        body: JSON.stringify({
          assessment: this.gameName,
          camera,
          microphone,
          screen,
          redirected
        } as CreatePermissions)
      }
    );
  }

  private getEnabledPermissions(): {
    cameraEnabled: boolean;
    microphoneEnabled: boolean;
    screenEnabled: boolean;
  } {
    const { cameraLive, audioLive, screenLive } = this.getSafariLiveState();
    return {
      cameraEnabled: !!(this.enabled && this.videoStream?.active && cameraLive),
      microphoneEnabled: !!(
        this.audioCapture &&
        this.audioStream?.active &&
        audioLive
      ),
      screenEnabled: !!(
        this.screenCapture &&
        this.screenStream?.active &&
        screenLive
      )
    };
  }

  private getSafariLiveState(): {
    cameraLive: boolean;
    audioLive: boolean;
    screenLive: boolean;
  } {
    const cameraTrack = this.videoStream?.getVideoTracks()[0];
    const audioTrack = this.audioStream?.getAudioTracks()[0];
    const screenTrack = this.screenStream?.getVideoTracks()[0];

    const cameraLive = Boolean(
      cameraTrack && cameraTrack.readyState === "live" && !cameraTrack.muted
    );
    const audioLive = Boolean(
      audioTrack && audioTrack.readyState === "live" && !audioTrack.muted
    );

    // Apply browser-specific logic for screen sharing
    const isChromeBasedBrowser = isChrome || isEdge;
    const screenLive = isChromeBasedBrowser
      ? Boolean(screenTrack && screenTrack.readyState === "live") // Chrome/Edge: don't check muted state
      : Boolean(
          screenTrack && screenTrack.readyState === "live" && !screenTrack.muted
        ); // Safari and others: check muted

    return { cameraLive, audioLive, screenLive };
  }
}
